import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  uploadJianyingProductionImage,
} from '@/features/orders/ordering/services/orderMediaService.js';
import { fetchJianyingDevices } from '@/features/systemManagement/jianying/services/jianyingDeviceService.js';
import { getJianyingDeviceSchedulingState } from '@/features/systemManagement/jianying/utils/jianyingDevices.js';
import {
  JIANYING_TEMPLATE_RUNTIME_STATUS,
} from '@/features/systemManagement/jianying/constants/jianyingTemplateStatus.js';
import {
  fetchJianyingTemplateAvailability,
} from '@/features/systemManagement/jianying/services/jianyingTemplateService.js';
import {
  canUseProductionAutomation,
} from '@/features/automation/utils/productionAccess.js';
import {
  fetchEligibleJianyingOrders,
  fetchJianyingOrderAutomation,
} from '../../../services/jianyingJobService.js';
import {
  cleanupJianyingJobAudio,
} from '../../../services/jianyingAudioUploadService.js';
import {
  cleanupJianyingJobVideo,
} from '../../../services/jianyingVideoUploadService.js';
import {
  applyJianyingCopyPreset,
  applyMissingJianyingImageDefaults,
  createJianyingRequestGuard,
  createInitialJianyingSlotValues,
  getFirstJianyingInvalidSlotKey,
  getFirstJianyingFieldErrorKey,
  getFirstJianyingImageFieldErrorKey,
  getJianyingAudioSlots,
  getJianyingDefaultCopyPresetId,
  getJianyingImageSlots,
  JIANYING_AI_CAPTION_TOKEN,
  getJianyingTextSlots,
  getJianyingVideoSlots,
  normalizeJianyingFieldErrors,
  normalizeJianyingOrderImages,
} from '../../../utils/jianyingSlots.js';

const MAX_PRODUCTION_IMAGE_BYTES = 100 * 1024 * 1024;

const PRODUCTION_UPLOAD_ERRORS = {
  image_file_invalid: '仅支持 JPG、JPEG、PNG 或 WebP 图片。',
  image_file_too_large: '单张图片不能超过 100MB。',
};

const hasSupportedImageFormat = (file) => (
  /\.(?:jpe?g|png|webp)$/i.test(String(file?.name || ''))
  && ['image/jpeg', 'image/png', 'image/webp']
    .includes(String(file?.type || '').toLowerCase())
);

const partitionJianyingProductionImageFiles = (files = []) => (
  Array.from(files || []).reduce((result, file) => {
    if (Number(file?.size || 0) > MAX_PRODUCTION_IMAGE_BYTES) {
      result.rejected.push({ file, code: 'image_file_too_large' });
    } else if (!hasSupportedImageFormat(file)) {
      result.rejected.push({ file, code: 'image_file_invalid' });
    } else {
      result.accepted.push(file);
    }
    return result;
  }, { accepted: [], rejected: [] })
);

const runJianyingProductionImageBatch = async (
  items,
  operation,
  isCurrent = () => true,
) => {
  const succeeded = [];
  const failed = [];
  for (const item of Array.isArray(items) ? items : []) {
    if (!isCurrent()) return { aborted: true, failed: [], succeeded: [] };
    try {
      const value = await operation(item);
      if (!isCurrent()) return { aborted: true, failed: [], succeeded: [] };
      succeeded.push({ item, value });
    } catch (error) {
      if (!isCurrent()) return { aborted: true, failed: [], succeeded: [] };
      failed.push({ item, error });
    }
  }
  return { aborted: false, failed, succeeded };
};

const getSafeFileName = (item = {}) => (
  String(item.file?.name || item.name || '未命名图片')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, 80) || '未命名图片'
);

const getSafeProductionUploadError = (failure = {}) => (
  PRODUCTION_UPLOAD_ERRORS[failure.code || failure.error?.code]
  || '上传失败，请重试。'
);

const formatJianyingProductionUploadNotice = (
  result,
  { refreshFailed = false, maxFailureLabels = 3 } = {},
) => {
  const succeeded = Array.isArray(result?.succeeded) ? result.succeeded : [];
  const failed = Array.isArray(result?.failed) ? result.failed : [];
  if (!failed.length) {
    return refreshFailed
      ? `已上传 ${succeeded.length} 张，但刷新图片失败，请重试。`
      : `上传完成，共 ${succeeded.length} 张。`;
  }
  const visible = failed.slice(0, maxFailureLabels).map((failure) => (
    `${getSafeFileName(failure.item)}（${getSafeProductionUploadError(failure)}）`
  ));
  const remaining = failed.length - visible.length;
  const refreshCopy = refreshFailed && succeeded.length ? '成功图片暂未刷新。' : '';
  return `上传完成：成功 ${succeeded.length} 张，失败 ${failed.length} 张。${refreshCopy}`
    + `失败项：${visible.join('、')}${remaining > 0 ? `，其余 ${remaining} 张` : ''}。`;
};

export const getJianyingCreateErrorMessage = (error, fallback) => (
  error.response?.data?.message || fallback
);

export const getJianyingTemplateRuntimeStatus = (template, availability) => (
  availability[template.id] || JIANYING_TEMPLATE_RUNTIME_STATUS.CHECKING
);

const useJianyingCreateWorkspace = ({ orderId, setSearchParams }) => {
  const roleId = Number(sessionStorage.getItem('role_id'));
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('');
  const [orderPickerExpanded, setOrderPickerExpanded] = useState(!orderId);
  const [templatePickerExpanded, setTemplatePickerExpanded] = useState(false);
  const [devicePickerExpanded, setDevicePickerExpanded] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersMessage, setOrdersMessage] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesMessage, setDevicesMessage] = useState('');
  const [templateAvailability, setTemplateAvailability] = useState({});
  const [templateAvailableDeviceIds, setTemplateAvailableDeviceIds] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [activeStep, setActiveStep] = useState(orderId ? 'template' : 'order');
  const [selectedCopyPresetId, setSelectedCopyPresetId] = useState('');
  const [preferredDeviceId, setPreferredDeviceId] = useState('');
  const [slotValues, setSlotValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [audioFieldErrors, setAudioFieldErrors] = useState({});
  const [imageFieldErrors, setImageFieldErrors] = useState({});
  const [videoFieldErrors, setVideoFieldErrors] = useState({});
  const [productionUploadStatus, setProductionUploadStatus] = useState({ state: 'idle' });
  const [loading, setLoading] = useState(Boolean(orderId));
  const [message, setMessage] = useState('');
  const fieldRefs = useRef(new Map());
  const imageFieldRefs = useRef(new Map());
  const orderRequestGuard = useRef(null);
  const productionRefreshRequestGuard = useRef(null);
  const productionUploadRequestGuard = useRef(null);
  const availabilityRequestId = useRef(0);
  const uploadedAudioKeys = useRef(new Set());
  const uploadedVideoKeys = useRef(new Set());
  const manuallyEditedTextSlotIds = useRef(new Set());
  if (!orderRequestGuard.current) {
    orderRequestGuard.current = createJianyingRequestGuard();
  }
  if (!productionRefreshRequestGuard.current) {
    productionRefreshRequestGuard.current = createJianyingRequestGuard();
  }
  if (!productionUploadRequestGuard.current) {
    productionUploadRequestGuard.current = createJianyingRequestGuard();
  }

  const canUseJianying = canUseProductionAutomation(roleId);

  const cleanupAudioKeys = useCallback((keys) => {
    const objectKeys = [...new Set(keys || [])].filter(Boolean);
    if (!orderId || objectKeys.length === 0) return;
    cleanupJianyingJobAudio({ clientId: orderId, objectKeys })
      .then(() => {
        objectKeys.forEach((key) => uploadedAudioKeys.current.delete(key));
      })
      .catch(() => {});
  }, [orderId]);

  const cleanupVideoKeys = useCallback((keys) => {
    const objectKeys = [...new Set(keys || [])].filter(Boolean);
    if (!orderId || objectKeys.length === 0) return;
    cleanupJianyingJobVideo({ clientId: orderId, objectKeys })
      .then(() => {
        objectKeys.forEach((key) => uploadedVideoKeys.current.delete(key));
      })
      .catch(() => {});
  }, [orderId]);

  useEffect(() => () => {
    cleanupAudioKeys([...uploadedAudioKeys.current]);
    cleanupVideoKeys([...uploadedVideoKeys.current]);
  }, [cleanupAudioKeys, cleanupVideoKeys]);

  useEffect(() => {
    if (!canUseJianying) {
      setOrdersLoading(false);
      return undefined;
    }
    let active = true;
    setOrdersLoading(true);
    fetchEligibleJianyingOrders()
      .then((data) => {
        if (active) setOrders(data);
      })
      .catch((error) => {
        if (active) setOrdersMessage(getJianyingCreateErrorMessage(error, '读取可制作订单失败'));
      })
      .finally(() => {
        if (active) setOrdersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canUseJianying]);

  const loadOrder = useCallback(async () => {
    const requestId = orderRequestGuard.current.begin();
    manuallyEditedTextSlotIds.current.clear();
    if (!orderId || !canUseJianying) {
      setClientInfo(null);
      setAutomation(null);
      setTemplateAvailability({});
      setTemplateAvailableDeviceIds({});
      setSelectedTemplateId('');
      setActiveStep('order');
      setPreferredDeviceId('');
      setSlotValues({});
      setFieldErrors({});
      setAudioFieldErrors({});
      setImageFieldErrors({});
      setVideoFieldErrors({});
      setProductionUploadStatus({ state: 'idle' });
      fieldRefs.current.clear();
      imageFieldRefs.current.clear();
      setOrderPickerExpanded(true);
      setTemplatePickerExpanded(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    availabilityRequestId.current += 1;
    setTemplateAvailability({});
    setTemplateAvailableDeviceIds({});
    setSelectedTemplateId('');
    setPreferredDeviceId('');
    setSlotValues({});
    setMessage('');
    setFieldErrors({});
    setAudioFieldErrors({});
    setImageFieldErrors({});
    setVideoFieldErrors({});
    setProductionUploadStatus({ state: 'idle' });
    fieldRefs.current.clear();
    imageFieldRefs.current.clear();
    try {
      const automationData = await fetchJianyingOrderAutomation(orderId);
      if (!orderRequestGuard.current.isCurrent(requestId)) return;
      setClientInfo({
        ...automationData.client,
        images: Array.isArray(automationData.images) ? automationData.images : [],
      });
      setAutomation(automationData);
      setActiveStep('template');
      setSelectedTemplateId('');
      setSlotValues({});
      setOrderPickerExpanded(false);
      setTemplatePickerExpanded(true);
    } catch (error) {
      if (!orderRequestGuard.current.isCurrent(requestId)) return;
      setClientInfo(null);
      setAutomation(null);
      setTemplateAvailability({});
      setTemplateAvailableDeviceIds({});
      setSelectedTemplateId('');
      setActiveStep('order');
      setPreferredDeviceId('');
      setSlotValues({});
      setOrderPickerExpanded(true);
      setTemplatePickerExpanded(false);
      setMessage(getJianyingCreateErrorMessage(error, '读取订单的剪映配置失败'));
    } finally {
      if (orderRequestGuard.current.isCurrent(requestId)) {
        setLoading(false);
      }
    }
  }, [canUseJianying, orderId]);

  useEffect(() => {
    loadOrder();
    return () => {
      orderRequestGuard.current.invalidate();
      productionRefreshRequestGuard.current.invalidate();
      productionUploadRequestGuard.current.invalidate();
    };
  }, [loadOrder]);

  const eligibleOrders = useMemo(() => {
    const keyword = orderFilter.trim().toLowerCase();
    if (!keyword) return orders;
    return orders.filter((order) => [
      order.client_id,
      order.client_name,
      order.video_type,
    ].some((value) => String(value || '').toLowerCase().includes(keyword)));
  }, [orderFilter, orders]);

  const selectedOrder = useMemo(
    () => orders.find((order) => String(order.client_id) === String(orderId)) || null,
    [orderId, orders],
  );
  const clientName = clientInfo?.clientName
    || clientInfo?.client_name
    || automation?.client?.clientName
    || selectedOrder?.client_name
    || orderId;

  const chooseOrder = (clientId) => {
    orderRequestGuard.current.invalidate();
    productionRefreshRequestGuard.current.invalidate();
    productionUploadRequestGuard.current.invalidate();
    setClientInfo(null);
    setAutomation(null);
    availabilityRequestId.current += 1;
    setTemplateAvailability({});
    setTemplateAvailableDeviceIds({});
    setSelectedTemplateId('');
    setActiveStep('template');
    setPreferredDeviceId('');
    setSlotValues({});
    setFieldErrors({});
    setAudioFieldErrors({});
    setImageFieldErrors({});
    setVideoFieldErrors({});
    setProductionUploadStatus({ state: 'idle' });
    cleanupAudioKeys([...uploadedAudioKeys.current]);
    cleanupVideoKeys([...uploadedVideoKeys.current]);
    fieldRefs.current.clear();
    imageFieldRefs.current.clear();
    setLoading(true);
    setMessage('');
    setOrderPickerExpanded(false);
    setTemplatePickerExpanded(true);
    setSearchParams({ client_id: String(clientId) });
  };

  const templates = useMemo(() => automation?.templates || [], [automation]);
  const orderImages = useMemo(
    () => normalizeJianyingOrderImages(clientInfo?.images || []),
    [clientInfo?.images],
  );

  const refreshProductionImages = useCallback(async () => {
    if (!orderId) return false;
    const requestId = productionRefreshRequestGuard.current.begin();
    try {
      const automationData = await fetchJianyingOrderAutomation(orderId);
      if (!productionRefreshRequestGuard.current.isCurrent(requestId)) return false;
      const images = Array.isArray(automationData.images) ? automationData.images : [];
      setClientInfo((current) => ({
        ...current,
        images,
      }));
      const refreshedTemplate = (automationData.templates || [])
        .find((template) => String(template.id) === String(selectedTemplateId));
      if (refreshedTemplate) {
        const normalizedImages = normalizeJianyingOrderImages(images);
        setSlotValues((current) => applyMissingJianyingImageDefaults({
          template: refreshedTemplate,
          slotValues: current,
          orderImages: normalizedImages,
        }));
      }
      return true;
    } catch (error) {
      return false;
    }
  }, [orderId, selectedTemplateId]);

  const uploadProductionImages = useCallback(async (files) => {
    if (!orderId || productionUploadStatus.state === 'uploading') return;
    const requestId = productionUploadRequestGuard.current.begin();
    const isCurrent = () => productionUploadRequestGuard.current.isCurrent(requestId);
    const { accepted, rejected } = partitionJianyingProductionImageFiles(files);
    const validationFailures = rejected.map(({ file, code }) => ({
      item: { file },
      error: { code },
    }));
    if (!accepted.length) {
      setProductionUploadStatus({
        state: 'complete',
        message: formatJianyingProductionUploadNotice({
          failed: validationFailures,
          succeeded: [],
        }),
      });
      return;
    }

    setProductionUploadStatus({ state: 'uploading', total: accepted.length });
    const result = await runJianyingProductionImageBatch(
      accepted.map((file) => ({ file })),
      ({ file }) => uploadJianyingProductionImage({
        clientId: orderId,
        file,
      }),
      isCurrent,
    );
    if (result.aborted || !isCurrent()) return;

    const refreshed = result.succeeded.length > 0
      ? await refreshProductionImages()
      : true;
    if (!isCurrent()) return;
    const completeResult = {
      failed: [...validationFailures, ...result.failed],
      succeeded: result.succeeded,
    };
    setProductionUploadStatus({
      state: 'complete',
      message: formatJianyingProductionUploadNotice(
        completeResult,
        { refreshFailed: !refreshed },
      ),
    });
  }, [orderId, productionUploadStatus.state, refreshProductionImages]);
  useEffect(() => {
    const requestId = availabilityRequestId.current + 1;
    availabilityRequestId.current = requestId;
    const templateIds = templates.map((template) => template.id);
    if (!orderId || templateIds.length === 0) {
      setTemplateAvailability({});
      setTemplateAvailableDeviceIds({});
      setPreferredDeviceId('');
      return undefined;
    }

    setTemplateAvailability(Object.fromEntries(
      templateIds.map((id) => [
        id,
        JIANYING_TEMPLATE_RUNTIME_STATUS.CHECKING,
      ]),
    ));
    setTemplateAvailableDeviceIds({});
    setSelectedTemplateId('');
    setPreferredDeviceId('');
    setSlotValues({});

    fetchJianyingTemplateAvailability(templateIds)
      .then((items) => {
        if (availabilityRequestId.current !== requestId) return;
        const responseStatuses = Object.fromEntries(
          items.map((item) => [item.template_id, item.status]),
        );
        setTemplateAvailableDeviceIds(Object.fromEntries(
          items.map((item) => [
            item.template_id,
            Array.isArray(item.available_device_ids)
              ? item.available_device_ids
              : [],
          ]),
        ));
        const nextAvailability = Object.fromEntries(
          templateIds.map((id) => [
            id,
            responseStatuses[id] || JIANYING_TEMPLATE_RUNTIME_STATUS.UNREADABLE,
          ]),
        );
        setTemplateAvailability(nextAvailability);

        const onlyTemplate = templates.length === 1
          && nextAvailability[templates[0].id]
            === JIANYING_TEMPLATE_RUNTIME_STATUS.AVAILABLE
          ? templates[0]
          : null;
        if (onlyTemplate) {
          setSelectedTemplateId(onlyTemplate.id);
          setActiveStep('image_processing');
          setSelectedCopyPresetId(getJianyingDefaultCopyPresetId(onlyTemplate));
          setSlotValues(createInitialJianyingSlotValues(
            onlyTemplate,
            clientName,
            orderImages,
          ));
          setTemplatePickerExpanded(false);
        }
      })
      .catch(() => {
        if (availabilityRequestId.current !== requestId) return;
        setTemplateAvailableDeviceIds({});
        setPreferredDeviceId('');
        setTemplateAvailability(Object.fromEntries(
          templateIds.map((id) => [
            id,
            JIANYING_TEMPLATE_RUNTIME_STATUS.SERVICE_OFFLINE,
          ]),
        ));
      });

    return () => {
      if (availabilityRequestId.current === requestId) {
        availabilityRequestId.current += 1;
      }
    };
  // Image refreshes must not reset the selected template or current slot selections.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName, orderId, templates]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => String(template.id) === String(selectedTemplateId)) || null,
    [selectedTemplateId, templates],
  );
  const preferredDevices = useMemo(() => {
    const availableIds = new Set(
      (templateAvailableDeviceIds[selectedTemplateId] || []).map(String),
    );
    return devices
      .filter(
        (device) =>
          getJianyingDeviceSchedulingState(device).schedulable
          && availableIds.has(String(device.id)),
      )
      .sort(
        (left, right) => Number(left.priority || 0) - Number(right.priority || 0)
          || String(left.name || '').localeCompare(String(right.name || ''), 'zh-CN'),
      );
  }, [devices, selectedTemplateId, templateAvailableDeviceIds]);
  const openDevicePicker = useCallback(() => {
    if (!selectedTemplate) return;
    setDevicePickerExpanded(true);
    setDevicesLoading(true);
    setDevicesMessage('');
    fetchJianyingDevices()
      .then((data) => setDevices(data))
      .catch((error) => {
        setDevices([]);
        setDevicesMessage(getJianyingCreateErrorMessage(
          error,
          '设备列表读取失败，仍可由系统自动选择。',
        ));
      })
      .finally(() => setDevicesLoading(false));
  }, [selectedTemplate]);
  useEffect(() => {
    if (
      preferredDeviceId
      && !preferredDevices.some(
        (device) => String(device.id) === String(preferredDeviceId),
      )
    ) {
      setPreferredDeviceId('');
    }
  }, [preferredDeviceId, preferredDevices]);
  const audioSlots = useMemo(
    () => getJianyingAudioSlots(selectedTemplate),
    [selectedTemplate],
  );
  const videoSlots = useMemo(
    () => getJianyingVideoSlots(selectedTemplate),
    [selectedTemplate],
  );
  const textSlots = useMemo(
    () => getJianyingTextSlots(selectedTemplate),
    [selectedTemplate],
  );
  const imageSlots = useMemo(
    () => getJianyingImageSlots(selectedTemplate),
    [selectedTemplate],
  );
  const chooseTemplate = (template) => {
    const runtimeStatus = getJianyingTemplateRuntimeStatus(template, templateAvailability);
    if (runtimeStatus !== JIANYING_TEMPLATE_RUNTIME_STATUS.AVAILABLE) return;
    cleanupAudioKeys([...uploadedAudioKeys.current]);
    cleanupVideoKeys([...uploadedVideoKeys.current]);
    setSelectedTemplateId(template.id);
    manuallyEditedTextSlotIds.current.clear();
    setActiveStep('image_processing');
    setSelectedCopyPresetId(getJianyingDefaultCopyPresetId(template));
    setTemplatePickerExpanded(false);
    setPreferredDeviceId('');
    setSlotValues(createInitialJianyingSlotValues(template, clientName, orderImages));
    setFieldErrors({});
    setAudioFieldErrors({});
    setImageFieldErrors({});
    setVideoFieldErrors({});
    fieldRefs.current.clear();
    imageFieldRefs.current.clear();
    setMessage('');
  };

  const showImageProcessing = () => {
    if (selectedTemplateId) setActiveStep('image_processing');
  };

  const showMaterials = () => {
    if (!selectedTemplateId) return;
    setActiveStep('materials');
  };

  const chooseCopyPreset = (presetId) => {
    if (!selectedTemplate) return;
    manuallyEditedTextSlotIds.current.clear();
    setSelectedCopyPresetId(presetId);
    setSlotValues((current) => applyJianyingCopyPreset(
      selectedTemplate,
      current,
      presetId,
      clientName,
    ));
    setFieldErrors({});
  };

  const setSlotValue = (slotKey, value) => {
    setSlotValues((current) => ({ ...current, [slotKey]: value }));
  };

  const setTextSlotValue = (slotKey, text, { manual = true } = {}) => {
    if (manual) manuallyEditedTextSlotIds.current.add(String(slotKey));
    else manuallyEditedTextSlotIds.current.delete(String(slotKey));
    setSlotValue(slotKey, { kind: 'text', text });
    setFieldErrors((current) => {
      if (!current[slotKey]) return current;
      const nextErrors = { ...current };
      delete nextErrors[slotKey];
      return nextErrors;
    });
  };

  const setAudioSlotValue = (slotKey, value) => {
    const previousObjectKey = slotValues[slotKey]?.object_key;
    if (previousObjectKey && previousObjectKey !== value?.object_key) {
      cleanupAudioKeys([previousObjectKey]);
    }
    setSlotValue(slotKey, value);
    setAudioFieldErrors((current) => {
      if (!current[slotKey]) return current;
      const nextErrors = { ...current };
      delete nextErrors[slotKey];
      return nextErrors;
    });
  };

  const setVideoSlotValue = (slotKey, value) => {
    const previousObjectKey = slotValues[slotKey]?.object_key;
    if (previousObjectKey && previousObjectKey !== value?.object_key) {
      cleanupVideoKeys([previousObjectKey]);
    }
    setSlotValue(slotKey, value);
    setVideoFieldErrors((current) => {
      if (!current[slotKey]) return current;
      const nextErrors = { ...current };
      delete nextErrors[slotKey];
      return nextErrors;
    });
  };

  const clearImageFieldError = (fieldKey) => {
    setImageFieldErrors((current) => {
      if (!current[fieldKey]) return current;
      const nextErrors = { ...current };
      delete nextErrors[fieldKey];
      return nextErrors;
    });
  };

  const setImageSlotValue = (slotKey, value) => {
    const imageSlot = selectedTemplate?.slots?.find(
      (slot) => String(slot.id) === String(slotKey),
    );
    const preset = selectedTemplate?.copy_presets?.find(
      (item) => String(item.id) === String(selectedCopyPresetId),
    );
    const aiTextSlotIds = (Array.isArray(imageSlot?.text_slot_ids)
      ? imageSlot.text_slot_ids
      : [])
      .map(String)
      .filter((id) => (
        preset?.values?.[id] === JIANYING_AI_CAPTION_TOKEN
        && !manuallyEditedTextSlotIds.current.has(id)
      ));
    setSlotValues((current) => {
      const next = { ...current, [slotKey]: value };
      aiTextSlotIds.forEach((id) => {
        next[id] = { kind: 'text', text: '' };
      });
      return next;
    });
    clearImageFieldError(slotKey);
  };

  const registerFieldRef = useCallback((slotKey, node) => {
    if (node) fieldRefs.current.set(slotKey, node);
    else fieldRefs.current.delete(slotKey);
  }, []);

  const registerImageFieldRef = useCallback((fieldKey, node) => {
    if (node) imageFieldRefs.current.set(fieldKey, node);
    else imageFieldRefs.current.delete(fieldKey);
  }, []);

  const applyFieldErrors = useCallback((rawFieldErrors, summaryMessage) => {
    const normalizedErrors = normalizeJianyingFieldErrors(rawFieldErrors);
    const relatedErrors = Object.fromEntries(
      textSlots.flatMap((slot) => (
        normalizedErrors[slot.key] ? [[slot.key, normalizedErrors[slot.key]]] : []
      )),
    );
    const firstErrorKey = getFirstJianyingFieldErrorKey(selectedTemplate, relatedErrors);

    setFieldErrors(relatedErrors);
    setMessage(summaryMessage);
    if (!firstErrorKey) return false;

    requestAnimationFrame(() => {
      const field = fieldRefs.current.get(firstErrorKey);
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field?.focus();
    });
    return true;
  }, [selectedTemplate, textSlots]);

  const applyImageFieldErrors = useCallback((
    nextImageFieldErrors,
    summaryMessage,
  ) => {
    const firstErrorKey = getFirstJianyingImageFieldErrorKey(
      selectedTemplate,
      nextImageFieldErrors,
    );
    setImageFieldErrors(nextImageFieldErrors);
    setMessage(summaryMessage);
    if (!firstErrorKey) return false;

    requestAnimationFrame(() => {
      const field = imageFieldRefs.current.get(firstErrorKey);
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field?.focus();
    });
    return true;
  }, [selectedTemplate]);

  const focusFirstInvalidField = useCallback(() => {
    const fieldKey = getFirstJianyingInvalidSlotKey(selectedTemplate, {
      audioFieldErrors,
      fieldErrors,
      imageFieldErrors,
      videoFieldErrors,
    });
    const field = fieldRefs.current.get(fieldKey) || imageFieldRefs.current.get(fieldKey);
    if (!field) return;
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    field.focus?.({ preventScroll: true });
  }, [
    audioFieldErrors,
    fieldErrors,
    imageFieldErrors,
    selectedTemplate,
    videoFieldErrors,
  ]);


  return {
    activeStep,
    applyFieldErrors,
    applyImageFieldErrors,
    audioFieldErrors,
    audioSlots,
    automation,
    canUseJianying,
    chooseCopyPreset,
    chooseOrder,
    chooseTemplate,
    clientName,
    devicePickerExpanded,
    devicesLoading,
    devicesMessage,
    eligibleOrders,
    fieldErrors,
    focusFirstInvalidField,
    imageFieldErrors,
    imageSlots,
    loading,
    message,
    openDevicePicker,
    orderFilter,
    orderImages,
    orderPickerExpanded,
    orders,
    ordersLoading,
    ordersMessage,
    preferredDeviceId,
    preferredDevices,
    productionUploadStatus,
    refreshProductionImages,
    registerFieldRef,
    registerImageFieldRef,
    selectedCopyPresetId,
    selectedTemplate,
    selectedTemplateId,
    setAudioFieldErrors,
    setAudioSlotValue,
    setDevicePickerExpanded,
    setFieldErrors,
    setImageFieldErrors,
    setImageSlotValue,
    setMessage,
    setOrderFilter,
    setOrderPickerExpanded,
    setPreferredDeviceId,
    setSlotValues,
    setTemplatePickerExpanded,
    setTextSlotValue,
    setVideoFieldErrors,
    setVideoSlotValue,
    showImageProcessing,
    showMaterials,
    slotValues,
    templateAvailability,
    templatePickerExpanded,
    templates,
    textSlots,
    uploadedAudioKeys,
    uploadedVideoKeys,
    uploadProductionImages,
    videoFieldErrors,
    videoSlots,
  };
};

export default useJianyingCreateWorkspace;
